import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import { IconMagnifyingGlass } from 'central-icons/IconMagnifyingGlass';
import { IconTrashCanSimple } from 'central-icons/IconTrashCanSimple';
import classNames from 'classnames';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { IconButton, Input, RevelioTooltip, Spinner, Text } from '@revelio/ui';

import { useGlobalAIAssistant } from './global-ai-assistant-context';
import {
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from './global-ai-assistant-engine-store';
import styles from './global-ai-conversation-list.module.css';

type GlobalAiConversationListProps = {
  /** Called after a conversation is successfully hydrated (e.g. close the drawer's history panel). */
  onSelected?: () => void;
};

/**
 * Searchable persisted-conversation list shared by the homepage sidebar and
 * the drawer history panel. Reads everything from the assistant context's
 * persistence API; owns only local search text and the pending-delete row.
 * Delete is a two-step inline confirm (never window.confirm — it blocks
 * headless automation).
 */
export function GlobalAiConversationList({
  onSelected,
}: GlobalAiConversationListProps) {
  const { persistence, activeConversationId, appendTranscript } =
    useGlobalAIAssistant();
  const {
    conversations,
    isConversationListLoading,
    isConversationListError,
    refetchConversationList,
    selectConversation,
    deleteConversation,
  } = persistence;

  const [search, setSearch] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);

  // Design: the active row's indicator bar sits ON TOP of the container's
  // right divider, replacing that segment of the line. It can't live inside
  // the scroll box: a classic (space-taking) scrollbar shrinks the rows'
  // content box, which would drag a row-anchored bar off the divider — and
  // overflow clipping makes the strip under the scrollbar unpaintable
  // anyway. So the bar is a sibling OVERLAY, projected onto the divider,
  // and this measures the active row to keep it vertically in sync,
  // clamped to the scroll viewport (the overlay's parent can't clip — it
  // must stay overflow-visible for the horizontal projection).
  // Direct style writes, not state: this runs per scroll event.
  const syncActiveIndicator = useCallback(() => {
    const scrollEl = scrollRef.current;
    const indicator = indicatorRef.current;
    if (!scrollEl || !indicator) {
      return;
    }
    const row = scrollEl.querySelector<HTMLElement>('[data-active]');
    if (!row) {
      indicator.style.display = 'none';
      return;
    }
    const scrollRect = scrollEl.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const top = Math.max(rowRect.top - scrollRect.top, 0);
    const bottom = Math.min(rowRect.bottom - scrollRect.top, scrollRect.height);
    if (bottom <= top) {
      indicator.style.display = 'none';
      return;
    }
    indicator.style.display = 'block';
    indicator.style.top = `${top}px`;
    indicator.style.height = `${bottom - top}px`;
  }, []);

  // No dep array on purpose: any commit can move the active row (list
  // refetch, search filter, delete confirm swapping row content, active
  // change). The measurement is a few rect reads over a small list.
  useLayoutEffect(() => {
    syncActiveIndicator();
  });

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return;
    }
    scrollEl.addEventListener('scroll', syncActiveIndicator, {
      passive: true,
    });
    // Guarded: jsdom has no ResizeObserver.
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(syncActiveIndicator);
    resizeObserver?.observe(scrollEl);
    return () => {
      scrollEl.removeEventListener('scroll', syncActiveIndicator);
      resizeObserver?.disconnect();
    };
  }, [syncActiveIndicator]);

  // A report offer launch writes back into the current assistant session and
  // is NOT aborted by hydrate/reset, so any history action that replaces or
  // resets the session must wait it out. Selecting is otherwise allowed
  // mid-turn (hydrateConversation aborts the turn first — abort-first), but
  // deleting the active chat calls newChat()/resetSession, which does NOT
  // abort, so delete additionally waits for the turn to finish.
  const isReportOfferLaunching = useGlobalAiEngineStore(
    (s) => s.reportOfferLaunchingKey !== null
  );
  const isTurnRunning = useGlobalAiEngineStore(selectIsTurnRunning);
  const selectBlocked = isReportOfferLaunching;
  const deleteBlocked = isTurnRunning || isReportOfferLaunching;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return conversations;
    }
    return conversations.filter((c) => c.title.toLowerCase().includes(q));
  }, [conversations, search]);

  const handleSelect = async (id: string) => {
    if (loadingId !== null || selectBlocked) {
      return;
    }
    setLoadingId(id);
    try {
      const result = await selectConversation(id);
      if (!result.ok) {
        appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'error',
          text:
            result.reason === 'newer_schema'
              ? 'This chat was saved by a newer version of the app. Refresh the page to open it.'
              : "Couldn't open that chat. Please try again.",
        });
        return;
      }
      onSelected?.();
    } finally {
      setLoadingId(null);
    }
  };

  const renderDeleteConfirm = (conversationId: string) => (
    <div className={styles.confirmRow}>
      <Text fontSize="x-sm" className={styles.confirmText}>
        Delete?
      </Text>
      <button
        type="button"
        className={styles.confirmBtn}
        disabled={deleteBlocked}
        onClick={() => {
          if (deleteBlocked) {
            return;
          }
          setPendingDeleteId(null);
          // The delete mutation opts out of global error handling, so a
          // failed DELETE would otherwise be a silent unhandled rejection
          // (the row just stays). Surface it like a select failure.
          void deleteConversation(conversationId).catch(() => {
            appendTranscript({
              id: crypto.randomUUID(),
              role: 'assistant',
              kind: 'error',
              text: "Couldn't delete that chat. Please try again.",
            });
          });
        }}
        data-testid="assistant-conversation-delete-confirm"
      >
        Delete
      </button>
      <button
        type="button"
        className={styles.cancelBtn}
        onClick={() => setPendingDeleteId(null)}
      >
        Cancel
      </button>
    </div>
  );

  const renderConversationRowContent = (
    conversation: (typeof visible)[number]
  ) => {
    const isRowLoading = loadingId === conversation.id;
    return (
      <>
        {/* Long openDelay (over the 400ms default): the full-title tooltip is
            a lean-in affordance for truncated rows, and at the default delay
            it fires constantly while just mousing across the list. */}
        <RevelioTooltip
          label={conversation.title}
          placement="top-start"
          openDelay={1200}
          className={styles.titleTooltip}
          triggerClassName={styles.rowTrigger}
        >
          <button
            type="button"
            className={styles.rowButton}
            onClick={() => void handleSelect(conversation.id)}
            disabled={loadingId !== null || selectBlocked}
          >
            <span className={styles.rowTitle}>{conversation.title}</span>
          </button>
        </RevelioTooltip>
        {isRowLoading ? (
          <Spinner className={styles.rowSpinner} size="xs" />
        ) : (
          <IconButton
            aria-label={`Delete chat ${conversation.title}`}
            icon={<IconTrashCanSimple size={14} />}
            size="sm"
            shape="rounded"
            className={styles.deleteBtn}
            disabled={deleteBlocked}
            onClick={() => setPendingDeleteId(conversation.id)}
            data-testid="assistant-conversation-delete"
          />
        )}
      </>
    );
  };

  const renderConversationRow = (conversation: (typeof visible)[number]) => {
    const isActive = conversation.id === activeConversationId;
    const isPendingDelete = conversation.id === pendingDeleteId;
    return (
      <div
        key={conversation.id}
        className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
        data-testid="assistant-conversation-item"
        data-active={isActive || undefined}
      >
        {isPendingDelete
          ? renderDeleteConfirm(conversation.id)
          : renderConversationRowContent(conversation)}
      </div>
    );
  };

  const renderListBody = () => {
    if (isConversationListLoading) {
      return (
        <div className={styles.loadingState}>
          <Spinner size="sm" />
        </div>
      );
    }
    if (isConversationListError) {
      return (
        <button
          type="button"
          className={styles.retryRow}
          onClick={refetchConversationList}
          data-testid="assistant-conversation-retry"
        >
          Couldn’t load chats — tap to retry
        </button>
      );
    }
    if (visible.length === 0) {
      return (
        <Text fontSize="x-sm" tone="muted" className={styles.emptyText}>
          {search.trim() ? 'No chats match' : 'No saved chats yet'}
        </Text>
      );
    }
    return (
      <>{visible.map((conversation) => renderConversationRow(conversation))}</>
    );
  };

  return (
    <div className={styles.listLayout}>
      <div className={styles.searchField}>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
          size="sm"
          className={styles.searchInput}
          wrapperClassName={styles.searchWrap}
          data-testid="assistant-conversation-search"
        />
        {search ? (
          <IconButton
            aria-label="Clear search"
            icon={<IconCrossSmall size={16} />}
            size="sm"
            shape="rounded"
            className={styles.searchClearBtn}
            onClick={() => setSearch('')}
            data-testid="assistant-conversation-search-clear"
          />
        ) : (
          <IconMagnifyingGlass size={16} className={styles.searchIcon} />
        )}
      </div>
      <div className={styles.listViewport}>
        <div
          ref={scrollRef}
          className={classNames(styles.listScroll, styles.listScrollArea)}
        >
          {renderListBody()}
        </div>
        <div
          ref={indicatorRef}
          className={styles.activeIndicator}
          aria-hidden
        />
      </div>
    </div>
  );
}
